import { extend, useFrame, useLoader, useThree } from "@react-three/fiber";
import { useMemo, useRef } from "react";
import * as THREE from "three";
import { Line2 } from "three/examples/jsm/lines/Line2.js";
import { LineGeometry } from "three/examples/jsm/lines/LineGeometry.js";
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial.js";
import { FontLoader } from "three/examples/jsm/loaders/FontLoader.js";
import { SVGLoader } from "three/examples/jsm/loaders/SVGLoader.js";
import { COURT_DIMENSIONS } from "./courtConstants";
import { isPointInPolygon, normalizeToWorldSpace } from "./courtUtils";

extend({ Line2, LineMaterial, LineGeometry });

const getRegionColor = (count, maxCount) => {
	if (count === 0) return "#2D3748"; // Dark gray for zones with no shots

	const t = Math.min(1, count / (maxCount || 1));

	if (t < 0.25) {
		return "#DDE1FF";
	} else if (t < 0.5) {
		return "#BEC6FF";
	} else if (t < 0.75) {
		return "#97A4FF";
	} else {
		return "#6B7BE8";
	}
};

const Outline = ({ shape, isSelected }) => {
	const { size } = useThree(); // Gets the canvas size
	const lineRef = useRef();

	// Add the breathing effect using useFrame
	useFrame(({ clock }) => {
		if (isSelected && lineRef.current) {
			// Use a sine wave for a smooth scaling animation
			const scale = 1 + 0.037 * Math.sin(clock.getElapsedTime() * 3);
			lineRef.current.scale.set(scale, scale, scale);
		}
	});

	// Create the fat line geometry from the edges of the shape
	const fatLineGeometry = useMemo(() => {
		const points = shape.getPoints();

		// Ensure the path is closed for a continuous outline
		if (points.length > 0) {
			const firstPoint = points[0];
			const lastPoint = points[points.length - 1];
			if (firstPoint.x !== lastPoint.x || firstPoint.y !== lastPoint.y) {
				points.push(firstPoint);
			}
		}

		const positions = points.flatMap((p) => [p.x, p.y, 0]);

		const lineGeo = new LineGeometry();
		lineGeo.setPositions(positions);
		return lineGeo;
	}, [shape]);

	const center = useMemo(() => {
		fatLineGeometry.computeBoundingBox();
		const centerVec = new THREE.Vector3();
		fatLineGeometry.boundingBox.getCenter(centerVec);
		return centerVec;
	}, [fatLineGeometry]);

	return (
		// Render the fat line
		<group ref={lineRef} position={[center.x, center.y, 0]}>
			<line2
				geometry={fatLineGeometry}
				position={[-center.x, -center.y, 0.2]}
				renderOrder={isSelected ? 2 : 1}
			>
				<lineMaterial
					color={isSelected ? "#FC9211" : "white"}
					linewidth={isSelected ? 6 : 2} // Make the selected line thicker
					resolution={[
						size.width * window.devicePixelRatio,
						size.height * window.devicePixelRatio,
					]} // Use device pixel ratio for sharper lines
					transparent
					opacity={isSelected ? 1 : 0.5} // Make non-selected outlines less prominent
					alphaToCoverage={true}
					depthTest={false}
					depthWrite={false}
				/>
			</line2>
		</group>
	);
};

export const RegionView = ({ shots = [], selectedRegion, onRegionClick }) => {
	const svg = useLoader(SVGLoader, "/img/heatmap/heatmap.svg");
	const font = useLoader(FontLoader, "/img/heatmap/JBsMono.json");

	const extrudeSettings = useMemo(
		() => ({
			steps: 2,
			depth: 0.2,
			bevelEnabled: false,
		}),
		[],
	);

	const shapes = useMemo(
		() => svg.paths.flatMap((path) => path.toShapes(true)),
		[svg],
	);

	const geometries = useMemo(
		() =>
			shapes.map((shape) => new THREE.ExtrudeGeometry(shape, extrudeSettings)),
		[shapes, extrudeSettings],
	);

	// Transform SVG regions to world space once (instead of transforming each shot to SVG space)
	const worldSpaceRegions = useMemo(() => {
		// These match the group transformations in the render below
		const scale = 0.0585;
		const position = new THREE.Vector3(
			COURT_DIMENSIONS.WIDTH / 2,
			0.02,
			-COURT_DIMENSIONS.HEIGHT / 2,
		);
		const rotation = new THREE.Euler(-Math.PI / 2, 0, Math.PI, "XYZ");

		// Create forward transformation matrix
		const matrix = new THREE.Matrix4();
		matrix.compose(
			position,
			new THREE.Quaternion().setFromEuler(rotation),
			new THREE.Vector3(scale, scale, scale),
		);

		// Transform each SVG region polygon to world space
		return shapes.map((shape) => {
			const svgPoints = shape.getPoints();
			return svgPoints.map((p) => {
				// SVG point in local space
				const localVec = new THREE.Vector3(p.x, p.y, 0);
				// Transform to world space
				const worldVec = localVec.applyMatrix4(matrix);
				// Return as 2D point for polygon testing (using x,y for 2D algorithm)
				return { x: worldVec.x, y: worldVec.z };
			});
		});
	}, [shapes]);

	// Calculate shot counts per region
	const regionCounts = useMemo(() => {
		const counts = new Array(shapes.length).fill(0);

		shots.forEach((shot) => {
			if (!shot.location) return;

			// Normalize to world space (handles both old {x,y} and new {x,y,z} formats)
			const worldLocation = normalizeToWorldSpace(shot.location);
			const shotPoint = { x: worldLocation.x, y: worldLocation.z };

			// Check which region the shot belongs to
			// Both shot and region polygons are now in the same world space!
			for (
				let regionIndex = 0;
				regionIndex < worldSpaceRegions.length;
				regionIndex++
			) {
				const polygon = worldSpaceRegions[regionIndex];

				if (isPointInPolygon(shotPoint, polygon)) {
					counts[regionIndex]++;
					break; // A shot belongs to only one region
				}
			}
		});

		return counts;
	}, [shots, worldSpaceRegions, shapes.length]);

	// Find max count for gradient scaling
	const maxCount = useMemo(() => {
		const max = Math.max(...regionCounts, 1);
		return max;
	}, [regionCounts]);

	return (
		<group
			position={[
				COURT_DIMENSIONS.WIDTH / 2,
				0.02,
				-COURT_DIMENSIONS.HEIGHT / 2,
			]}
			rotation={[-Math.PI / 2, 0, Math.PI]}
			scale={0.0585}
		>
			{geometries.map((geometry, index) => {
				const regionColor = getRegionColor(regionCounts[index], maxCount);
				const opacity = regionCounts[index] > 0 ? 0.7 : 0.3;

				return (
					<group key={index} onClick={() => onRegionClick?.(index)}>
						<mesh geometry={geometry}>
							<meshBasicMaterial
								color={regionColor}
								opacity={opacity}
								transparent
								side={THREE.DoubleSide}
							/>
						</mesh>
						<Outline
							geometry={geometry}
							index={index}
							font={font}
							shape={shapes[index]}
							isSelected={selectedRegion === index}
						/>
					</group>
				);
			})}
		</group>
	);
};
